import {inject, Injectable} from '@angular/core';
import {collection, collectionData, doc, docData, Firestore, orderBy, query} from '@angular/fire/firestore';
import {ID_FIELD_OPTIONS} from '@damofire/data-commons';
import {AuthDtoSignerService, AuthenticationService} from '@damofire/data-security';
import {Observable} from 'rxjs';
import {
  AgentMessage,
  AgentRequest,
  WW_IA_AGENT_MESSAGES_COLLECTION,
  WW_IA_AGENT_REQUESTS_COLLECTION
} from './agent.dto';
import {Warehouse} from '../warehouse/warehouse.dto';

@Injectable({ providedIn: 'root' })
export class WarehouseAgentService {
  private readonly firestore = inject(Firestore);
  private readonly dtoSigner = inject(AuthDtoSignerService);
  private readonly authService = inject(AuthenticationService);

  async createRequest(warehouse: Warehouse): Promise<string> {
    const user = await this.authService.currentUser();
    const request: AgentRequest = {
      userId: user._id,
      status: 'pending',
      organization: warehouse.organization,
      division: { _id: warehouse._id, name: warehouse.name },
    };
    const ref = await this.dtoSigner.add(request, WW_IA_AGENT_REQUESTS_COLLECTION);
    return ref.id;
  }

  async addMessage(requestId: string, content: string): Promise<void> {
    const message: AgentMessage = { role: 'user', content };
    await this.dtoSigner.groupAdd(message, WW_IA_AGENT_REQUESTS_COLLECTION, requestId, WW_IA_AGENT_MESSAGES_COLLECTION);
  }

  observeMessages(requestId: string): Observable<AgentMessage[]> {
    const messagesRef = collection(this.firestore, WW_IA_AGENT_REQUESTS_COLLECTION, requestId, WW_IA_AGENT_MESSAGES_COLLECTION);
    const messagesQuery = query(messagesRef, orderBy('audit.lastModifiedDate', 'asc'));
    return collectionData(messagesQuery, ID_FIELD_OPTIONS) as Observable<AgentMessage[]>;
  }

  observeRequest(requestId: string): Observable<AgentRequest> {
    const requestRef = doc(this.firestore, WW_IA_AGENT_REQUESTS_COLLECTION, requestId);
    return docData<AgentRequest>(requestRef as never, ID_FIELD_OPTIONS);
  }
}
